import { apiGet, apiPut } from './api';
import {
  ProductItem,
  LinkProductRequest,
  ApiPaginatedResponse,
  RootProductItem,
  BaseProductItem,
  BaseProductCard,
  LinkBaseProductRequest,
  LinkBaseProductResponse,
  UnlinkBaseProductResponse,
} from '@/lib/types';

export const getProductsList = async (
  page: number,
  pageSize: number,
  query?: string,
  category?: string,
  source?: string
) => {
  const url = `/products/me?page=${page}&page_size=${pageSize}${query ? `&q=${query}` : ''}${category ? `&category=${category}` : ''}${source ? `&source=${source}` : ''}`;
  const response = await apiGet<ApiPaginatedResponse<ProductItem>>(url);
  return response;
};

/**
 * Get root products with pagination and optional search
 */
export const getRootProducts = async (
  page = 1,
  pageSize = 100,
  searchQuery?: string
) => {
  const params = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
  });
  if (searchQuery) {
    params.append('q', searchQuery);
  }
  return apiGet<ApiPaginatedResponse<RootProductItem>>(
    `/products/root?${params}`
  );
};

/**
 * Search base products
 */
export const searchBaseProducts = async (query: string, limit = 20) => {
  const params = new URLSearchParams({
    q: query,
    limit: limit.toString(),
  });
  return apiGet<
    Array<{
      normalized_name: string;
      brand?: string | null;
      origin?: string | null;
      dominant_pricing_type?: string | null;
      avg_unit_price?: number | null;
      min_unit_price?: number | null;
      max_unit_price?: number | null;
      last_seen?: string | null;
    }>
  >(`/base-products/search?${params}`);
};

/**
 * Get base products for a specific root product
 * @param rootProductId - The ID of the root product
 * @returns Promise that resolves to base products for the root product
 */
export const getBaseProductsByRoot = async (rootProductId: number) => {
  const params = new URLSearchParams({
    ids: rootProductId.toString(),
  });
  return apiGet<
    Array<{
      id: number;
      name: string;
      products: Array<{
        id: string;
        normalized_name: string;
        origin?: string | null;
        is_imported?: boolean;
      }>;
    }>
  >(`/products/root/base-products?${params}`);
};

/**
 * Get base products for multiple root products
 * @param rootProductIds - Array of root product IDs
 * @returns Promise that resolves to base products for all root products
 */
export const getBaseProductsByRoots = async (
  rootProductIds: number[]
): Promise<
  Array<{
    id: number;
    name: string;
    products: Array<BaseProductItem>;
  }>
> => {
  if (rootProductIds.length === 0) {
    return [];
  }
  const idsParams = rootProductIds.map(id => `ids=${id}`).join('&');
  return apiGet<
    Array<{
      id: number;
      name: string;
      products: Array<{
        id: string;
        normalized_name: string;
        origin?: string | null;
      }>;
    }>
  >(`/products/root/base-products?${idsParams}`);
};

/**
 * Link a root product to other root products
 * @param rootProductId - The ID of the root product to link
 * @param linkData - The product IDs to link to (array of strings)
 * @returns Promise that resolves when the link operation completes
 */
export const linkRootProduct = async (
  rootProductId: number,
  linkData: LinkProductRequest
): Promise<void> => {
  return apiPut<void>(`/products/root/${rootProductId}/link`, linkData);
};

/**
 * Base products for the linking cards, with the count of products linked to each
 */
export const getBaseProductCards = async ({
  page = 1,
  pageSize = 30,
  q,
  onlyMains = false,
  excludeIds = [],
  linkedTo,
}: {
  page?: number;
  pageSize?: number;
  q?: string;
  onlyMains?: boolean;
  excludeIds?: string[];
  linkedTo?: string;
}) => {
  const params = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
  });
  if (q) params.append('q', q);
  if (onlyMains) params.append('only_mains', 'true');
  if (linkedTo) params.append('linked_to', linkedTo);
  excludeIds.forEach(id => params.append('exclude_ids', id));
  return apiGet<ApiPaginatedResponse<BaseProductCard>>(
    `/products/base?${params}`
  );
};

/**
 * Link duplicate base products into a main base product
 * @param mainId - The main base product (must not be linked itself)
 * @param linkData - The duplicate ids to link into the main, plus an optional note
 * @returns Per-item results (linked / already_linked / blocked / not_found)
 */
export const linkBaseProduct = async (
  mainId: string,
  linkData: LinkBaseProductRequest
): Promise<LinkBaseProductResponse> => {
  return apiPut<LinkBaseProductResponse>(
    `/products/base/${mainId}/link`,
    linkData
  );
};

/**
 * Unlink base products from a main base product (only those linked to it)
 * @param mainId - The main base product
 * @param linkData - The linked product ids to detach, plus an optional note
 * @returns Per-item results (unlinked / not_linked / not_found)
 */
export const unlinkBaseProduct = async (
  mainId: string,
  linkData: LinkBaseProductRequest
): Promise<UnlinkBaseProductResponse> => {
  return apiPut<UnlinkBaseProductResponse>(
    `/products/base/${mainId}/unlink`,
    linkData
  );
};
